import { setToArray, Observable, assign, getRelativeTime, ONE_MINUTE, ContextHistory, addEventListener, generateUUID, clocksNow, ONE_SECOND, elapsed, } from '@datadog/browser-core';
import { trackEventCounts } from '../../trackEventCounts';
import { waitPageActivityEnd } from '../../waitPageActivityEnd';
import { createRageClickChain } from './rageClickChain';
import { getActionNameFromElement } from './getActionNameFromElement';
// Maximum duration for click actions
export var CLICK_ACTION_MAX_DURATION = 10 * ONE_SECOND;
export var ACTION_CONTEXT_TIME_OUT_DELAY = 5 * ONE_MINUTE; // arbitrary
export function trackClickActions(lifeCycle, domMutationObservable, _a) {
    var actionNameAttribute = _a.actionNameAttribute, trackFrustrations = _a.trackFrustrations;
    var history = new ContextHistory(ACTION_CONTEXT_TIME_OUT_DELAY);
    var stopObservable = new Observable();
    var currentRageClickChain;
    lifeCycle.subscribe(8 /* SESSION_RENEWED */, function () {
        history.reset();
    });
    lifeCycle.subscribe(9 /* BEFORE_UNLOAD */, stopRageClickChain);
    lifeCycle.subscribe(4 /* VIEW_ENDED */, stopRageClickChain);
    var stopListener = listenClickEvents(processClick).stop;
    var actionContexts = {
        findActionId: function (startTime) {
            return trackFrustrations ? history.findAll(startTime) : history.find(startTime);
        },
    };
    return {
        stop: function () {
            stopRageClickChain();
            stopObservable.notify();
            stopListener();
        },
        actionContexts: actionContexts,
    };
    function stopRageClickChain() {
        if (currentRageClickChain) {
            currentRageClickChain.stop();
        }
    }
    function processClick(event) {
        if (!trackFrustrations && history.find()) {
            // TODO: remove this in a future major version. To keep retrocompatibility, ignore any new
            // action if another one is already occurring.
            return;
        }
        var name = getActionNameFromElement(event.target, actionNameAttribute);
        if (!trackFrustrations && !name) {
            // TODO: remove this in a future major version. To keep retrocompatibility, ignore any action
            // with a blank name
            return;
        }
        var startClocks = clocksNow();
        var click = newClick(lifeCycle, history, trackFrustrations, {
            name: name,
            event: event,
            startClocks: startClocks,
        });
        if (trackFrustrations && (!currentRageClickChain || !currentRageClickChain.tryAppend(click))) {
            currentRageClickChain = createRageClickChain(click);
        }
        var stopWaitPageActivityEnd = waitPageActivityEnd(lifeCycle, domMutationObservable, function (pageActivityEndEvent) {
            if (!pageActivityEndEvent.hadActivity) {
                // If it has no activity, consider it as a dead click.
                // TODO: this will yield a lot of false positive. We'll need to refine it in the future.
                if (trackFrustrations) {
                    click.addFrustration("dead" /* DEAD */);
                    click.stop();
                }
                else {
                    click.discard();
                }
            }
            else if (pageActivityEndEvent.end < startClocks.timeStamp) {
                // If the clock is looking weird, just discard the click
                click.discard();
            }
            else if (trackFrustrations) {
                // If we track frustrations, let's stop the click, but validate it later
                click.stop(pageActivityEndEvent.end);
            }
            else {
                // Else just validate it now
                click.validate(pageActivityEndEvent.end);
            }
        }, CLICK_ACTION_MAX_DURATION).stop;
        var viewEndedSubscription = lifeCycle.subscribe(4 /* VIEW_ENDED */, function (_a) {
            var endClocks = _a.endClocks;
            click.stop(endClocks.timeStamp);
        });
        var stopSubscription = stopObservable.subscribe(function () {
            click.stop();
        });
        click.stopObservable.subscribe(function () {
            viewEndedSubscription.unsubscribe();
            stopWaitPageActivityEnd();
            stopSubscription.unsubscribe();
        });
    }
}
function listenClickEvents(callback) {
    return addEventListener(window, "click" /* CLICK */, function (clickEvent) {
        if (clickEvent.target instanceof Element) {
            callback(clickEvent);
        }
    }, { capture: true });
}
function newClick(lifeCycle, history, trackFrustrations, base) {
    var id = generateUUID();
    var historyEntry = history.add(id, base.startClocks.relative);
    var eventCountsSubscription = trackEventCounts(lifeCycle);
    var state = { status: 0 /* ONGOING */ };
    var frustrations = new Set();
    var stopObservable = new Observable();
    function stop(endTime) {
        if (state.status !== 0 /* ONGOING */) {
            return;
        }
        state = { status: 1 /* STOPPED */, endTime: endTime };
        if (endTime) {
            historyEntry.close(getRelativeTime(endTime));
        }
        else {
            historyEntry.remove();
        }
        eventCountsSubscription.stop();
        stopObservable.notify();
    }
    function addFrustration(frustration) {
        if (trackFrustrations) {
            frustrations.add(frustration);
        }
    }
    return {
        event: base.event,
        addFrustration: addFrustration,
        stop: stop,
        stopObservable: stopObservable,
        getFrustrations: function () { return frustrations; },
        isStopped: function () { return state.status === 1 /* STOPPED */ || state.status === 2 /* FINALIZED */; },
        clone: function () { return newClick(lifeCycle, history, trackFrustrations, base); },
        validate: function (endTime) {
            stop(endTime);
            if (state.status !== 1 /* STOPPED */) {
                return;
            }
            if (eventCountsSubscription.eventCounts.errorCount > 0) {
                addFrustration("error" /* ERROR */);
            }
            var _a = eventCountsSubscription.eventCounts, resourceCount = _a.resourceCount, errorCount = _a.errorCount, longTaskCount = _a.longTaskCount;
            var clickAction = assign({
                type: "click" /* CLICK */,
                duration: state.endTime && elapsed(base.startClocks.timeStamp, state.endTime),
                id: id,
                frustrationTypes: setToArray(frustrations),
                counts: {
                    resourceCount: resourceCount,
                    errorCount: errorCount,
                    longTaskCount: longTaskCount,
                },
            }, base);
            lifeCycle.notify(1 /* AUTO_ACTION_COMPLETED */, clickAction);
            state = { status: 2 /* FINALIZED */ };
        },
        discard: function () {
            stop();
            state = { status: 2 /* FINALIZED */ };
        },
    };
}
//# sourceMappingURL=trackClickActions.js.map